"use client";

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import { GoogleOAuthSetup } from "./google/GoogleOAuthSetup";
import { MicrosoftOAuthSetup } from "./microsoft/MicrosoftOAuthSetup";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../card";
import { Button } from "../button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../tabs";
import { Badge } from "../badge";
import { 
  Calendar, 
  Mail, 
  Settings, 
  Edit, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Users,
  Shield,
  Clock,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { useAccountStatus, useRemoveConnectionMutation } from "../../hooks/use-api";
import { ConfirmationModal } from "../confirmation-modal";

interface SetupPageProps {
  redirectUri?: string;
}

export default function SetupPage({ redirectUri }: SetupPageProps = {}) {
  const [activeTab, setActiveTab] = useState("google");
  const [removingConnection, setRemovingConnection] = useState<string | null>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    connectionType: string | null;
    title: string;
    description: string;
  }>({
    isOpen: false,
    connectionType: null,
    title: "",
    description: "",
  });
  const { toast } = useToast();
  const { data: accountStatus, isLoading: statusLoading, refetch: refetchStatus } = useAccountStatus();
  const removeConnectionMutation = useRemoveConnectionMutation();
  const searchParams = useSearchParams();

  // Handle URL parameters for direct tab navigation
  useEffect(() => {
    const tab = searchParams?.get('tab');
    if (tab === 'google' || tab === 'microsoft') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleSuccess = useCallback((provider: string) => {
    toast({
      title: "Success",
      description: `${provider} calendar connected successfully!`,
    });
    // Refetch status to update the UI
    refetchStatus();
  }, [toast, refetchStatus]);

  const handleError = useCallback((error: string) => {
    toast({
      title: "Error",
      description: error,
      variant: "destructive",
    });
  }, [toast]);

  const showConfirmationModal = (connectionType: 'google' | 'microsoft' | 'google-primary' | 'google-secondary') => {
    const getConnectionInfo = (type: string) => {
      switch (type) {
        case 'google-primary':
          return {
            title: "Remove Google Primary Account",
            description: `Are you sure you want to remove your primary Google account (${accountStatus?.google.primaryEmail || 'this account'})? This will disconnect your main Google Calendar integration.`,
          };
        case 'google-secondary':
          return {
            title: "Remove Google Secondary Account", 
            description: `Are you sure you want to remove your secondary Google account (${accountStatus?.google.secondaryEmail || 'this account'})? This will disconnect your additional Google Calendar integration.`,
          };
        case 'microsoft':
          return {
            title: "Remove Microsoft Account",
            description: `Are you sure you want to remove your Microsoft account (${accountStatus?.microsoftEmail || 'this account'})? This will disconnect your Outlook/Office 365 integration.`,
          };
        case 'google':
          return {
            title: "Remove All Connections",
            description: "Are you sure you want to remove all calendar connections? This will disconnect all your Google and Microsoft accounts. This action cannot be undone.",
          };
        default:
          return {
            title: "Remove Connection",
            description: "Are you sure you want to remove this connection?",
          };
      }
    };

    const info = getConnectionInfo(connectionType);
    setConfirmationModal({
      isOpen: true,
      connectionType,
      title: info.title,
      description: info.description,
    });
  };

  const handleConfirmRemove = async () => {
    if (!confirmationModal.connectionType) return;
    
    setRemovingConnection(confirmationModal.connectionType);
    try {
      if (confirmationModal.connectionType === 'google') {
        // Remove all connections - both Google and Microsoft
        const promises = [];
        
        // Remove Google connections if they exist
        if (accountStatus?.google.primary || accountStatus?.google.secondary) {
          promises.push(removeConnectionMutation.mutateAsync('google'));
        }
        
        // Remove Microsoft connection if it exists
        if (accountStatus?.microsoft) {
          promises.push(removeConnectionMutation.mutateAsync('microsoft'));
        }
        
        // Execute all removals in parallel
        await Promise.all(promises);
      } else {
        // Remove single connection
        await removeConnectionMutation.mutateAsync(confirmationModal.connectionType as any);
      }
      
      setConfirmationModal({ isOpen: false, connectionType: null, title: "", description: "" });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
      console.error('Failed to remove connection:', error);
    } finally {
      setRemovingConnection(null);
    }
  };

  const handleCloseConfirmation = () => {
    setConfirmationModal({ isOpen: false, connectionType: null, title: "", description: "" });
  };

  const getStatusBadge = (isConnected: boolean) => {
    if (isConnected) {
      return (
        <Badge variant="default" className="bg-green-600 text-white border-2 border-green-800 shadow-lg hover:shadow-xl transition-all duration-200 font-bold text-sm px-3 py-1">
          <CheckCircle className="h-4 w-4 mr-1" />
          CONNECTED
        </Badge>
      );
    }
    
return (
      <Badge variant="outline" className="bg-red-100 text-red-800 border-2 border-red-400 hover:bg-red-200 transition-all duration-200 font-bold text-sm px-3 py-1">
        <XCircle className="h-4 w-4 mr-1" />
        NOT CONNECTED
      </Badge>
    );
  };

  const getOverallStatus = () => {
    if (statusLoading) {
      return (
        <div className="flex items-center space-x-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Checking status...</span>
        </div>
      );
    }

    if (!accountStatus) {
      return (
        <div className="flex items-center space-x-2 text-muted-foreground">
          <XCircle className="h-4 w-4" />
          <span>Unable to check status</span>
        </div>
      );
    }

    const totalConnections = (accountStatus.google.primary ? 1 : 0) + 
                           (accountStatus.google.secondary ? 1 : 0) + 
                           (accountStatus.microsoft ? 1 : 0);
    
    if (totalConnections === 0) {
      return (
        <div className="flex items-center space-x-2 text-red-600 font-medium">
          <XCircle className="h-4 w-4" />
          <span>No accounts connected</span>
        </div>
      );
    } else if (totalConnections === 3) {
      return (
        <div className="flex items-center space-x-2 text-green-600 font-medium">
          <CheckCircle className="h-4 w-4" />
          <span>All accounts connected</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-2 text-blue-600 font-medium">
          <Users className="h-4 w-4" />
          <span>{totalConnections} of 3 accounts connected</span>
        </div>
      );
    }
  };

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3">
          <div className="p-3 bg-blue-100 rounded-full">
            <Settings className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Setup & Settings
            </h1>
            <p className="text-lg text-muted-foreground mt-1">
              Configure your calendar integrations and preferences
            </p>
          </div>
        </div>
        
        {/* Overall Status Card */}
        <Card className="max-w-md mx-auto border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-200 rounded-lg shadow-sm">
                  <Shield className="h-5 w-5 text-blue-700" />
                </div>
                <span className="font-bold text-blue-900">Account Status</span>
              </div>
              {getOverallStatus()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Status Overview */}
      {accountStatus && (
        <Card className="border-2 border-gray-200 bg-card">
          <CardHeader className="bg-card-50">
            <CardTitle className="flex items-center space-x-2 text-gray-800">
              <Users className="h-5 w-5 text-blue-600" />
              <span>Account Configuration</span>
            </CardTitle>
            <CardDescription className="text-gray-600">
              Current status of your calendar integrations
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Google Primary */}
              <div className={`relative overflow-hidden rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                accountStatus.google.primary 
                  ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md' 
                  : 'border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100'
              }`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${
                      accountStatus.google.primary 
                        ? 'bg-blue-200 shadow-sm' 
                        : 'bg-card-200'
                    }`}>
                      <Mail className={`h-6 w-6 ${
                        accountStatus.google.primary ? 'text-blue-700' : 'text-gray-500'
                      }`} />
                    </div>
                    {getStatusBadge(accountStatus.google.primary)}
                  </div>
                  <div className="space-y-2">
                    <h3 className={`text-lg font-bold ${
                      accountStatus.google.primary ? 'text-blue-900' : 'text-gray-600'
                    }`}>
                      Google Primary
                    </h3>
                    <p className={`text-sm font-medium ${
                      accountStatus.google.primary ? 'text-blue-700' : 'text-gray-500'
                    }`}>
                      {accountStatus.google.primaryEmail || "Main Google account"}
                    </p>
                    {accountStatus.google.primary && (
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-xs text-green-600 font-medium">Active</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                          onClick={() => showConfirmationModal('google-primary')}
                          disabled={removingConnection === 'google-primary'}
                        >
                          {removingConnection === 'google-primary' ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {accountStatus.google.primary && (
                  <div className="absolute top-0 right-0 w-20 h-20 bg-blue-200 rounded-full -translate-y-10 translate-x-10 opacity-20"></div>
                )}
              </div>

              {/* Google Secondary */}
              <div className={`relative overflow-hidden rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                accountStatus.google.secondary 
                  ? 'border-green-300 bg-gradient-to-br from-green-50 to-green-100 shadow-md' 
                  : 'border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100'
              }`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${
                      accountStatus.google.secondary 
                        ? 'bg-green-200 shadow-sm' 
                        : 'bg-card-200'
                    }`}>
                      <Users className={`h-6 w-6 ${
                        accountStatus.google.secondary ? 'text-green-700' : 'text-gray-500'
                      }`} />
                    </div>
                    {getStatusBadge(accountStatus.google.secondary)}
                  </div>
                  <div className="space-y-2">
                    <h3 className={`text-lg font-bold ${
                      accountStatus.google.secondary ? 'text-green-900' : 'text-gray-600'
                    }`}>
                      Google Secondary
                    </h3>
                    <p className={`text-sm font-medium ${
                      accountStatus.google.secondary ? 'text-green-700' : 'text-gray-500'
                    }`}>
                      {accountStatus.google.secondaryEmail || "Additional Google account"}
                    </p>
                    {accountStatus.google.secondary && (
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-xs text-green-600 font-medium">Active</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                          onClick={() => showConfirmationModal('google-secondary')}
                          disabled={removingConnection === 'google-secondary'}
                        >
                          {removingConnection === 'google-secondary' ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {accountStatus.google.secondary && (
                  <div className="absolute top-0 right-0 w-20 h-20 bg-green-200 rounded-full -translate-y-10 translate-x-10 opacity-20"></div>
                )}
              </div>

              {/* Microsoft */}
              <div className={`relative overflow-hidden rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                accountStatus.microsoft 
                  ? 'border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100 shadow-md' 
                  : 'border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100'
              }`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${
                      accountStatus.microsoft 
                        ? 'bg-orange-200 shadow-sm' 
                        : 'bg-card-200'
                    }`}>
                      <Calendar className={`h-6 w-6 ${
                        accountStatus.microsoft ? 'text-orange-700' : 'text-gray-500'
                      }`} />
                    </div>
                    {getStatusBadge(accountStatus.microsoft)}
                  </div>
                  <div className="space-y-2">
                    <h3 className={`text-lg font-bold ${
                      accountStatus.microsoft ? 'text-orange-900' : 'text-gray-600'
                    }`}>
                      Microsoft
                    </h3>
                    <p className={`text-sm font-medium ${
                      accountStatus.microsoft ? 'text-orange-700' : 'text-gray-500'
                    }`}>
                      {accountStatus.microsoftEmail || "Outlook/Office 365"}
                    </p>
                    {accountStatus.microsoft && (
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-xs text-green-600 font-medium">Active</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                          onClick={() => showConfirmationModal('microsoft')}
                          disabled={removingConnection === 'microsoft'}
                        >
                          {removingConnection === 'microsoft' ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {accountStatus.microsoft && (
                  <div className="absolute top-0 right-0 w-20 h-20 bg-orange-200 rounded-full -translate-y-10 translate-x-10 opacity-20"></div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup Actions */}
      <Card className="border-2 border-gray-200 bg-card">
        <CardHeader className="bg-card-50">
          <CardTitle className="flex items-center space-x-2 text-gray-800">
            <Settings className="h-5 w-5 text-blue-600" />
            <span>Connect Your Accounts</span>
          </CardTitle>
          <CardDescription className="text-gray-600">
            Set up your calendar integrations to get started with UnifiedTime
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-card-100 border-2 border-gray-400 shadow-lg p-2 gap-1">
              <TabsTrigger 
                value="google" 
                className={`flex items-center space-x-2 transition-all duration-300 rounded-lg p-3 ${
                  activeTab === 'google' 
                    ? 'bg-blue-600 text-white shadow-xl border-2 border-blue-800 font-bold scale-105 ring-2 ring-blue-300' 
                    : 'bg-card text-gray-700 hover:bg-blue-50 hover:text-blue-700 border border-gray-300'
                }`}
              >
                <Mail className="h-4 w-4" />
                <span>Google Calendar</span>
                {accountStatus && (
                  <Badge 
                    variant={accountStatus.google.primary || accountStatus.google.secondary ? "default" : "secondary"}
                    className={`ml-2 text-xs font-bold border-2 shadow-md px-2 py-1 ${
                      accountStatus.google.primary || accountStatus.google.secondary 
                        ? "bg-green-600 text-white border-green-800" 
                        : "bg-red-200 text-red-800 border-red-400"
                    }`}
                  >
                    {accountStatus.google.primary && accountStatus.google.secondary ? "2/2" : 
                     accountStatus.google.primary || accountStatus.google.secondary ? "1/2" : "0/2"}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="microsoft"
                className={`flex items-center space-x-2 transition-all duration-300 rounded-lg p-3 ${
                  activeTab === 'microsoft' 
                    ? 'bg-orange-600 text-white shadow-xl border-2 border-orange-800 font-bold scale-105 ring-2 ring-orange-300' 
                    : 'bg-card text-gray-700 hover:bg-orange-50 hover:text-orange-700 border border-gray-300'
                }`}
              >
                <Calendar className="h-4 w-4" />
                <span>Microsoft Calendar</span>
                {accountStatus && (
                  <Badge 
                    variant={accountStatus.microsoft ? "default" : "secondary"}
                    className={`ml-2 text-xs font-bold border-2 shadow-md px-2 py-1 ${
                      accountStatus.microsoft 
                        ? "bg-green-600 text-white border-green-800" 
                        : "bg-red-200 text-red-800 border-red-400"
                    }`}
                  >
                    {accountStatus.microsoft ? "1/1" : "0/1"}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="google">
              <GoogleOAuthSetup
                onSuccess={() => handleSuccess("Google")}
                onError={handleError}
                redirectUri={redirectUri}
              />
            </TabsContent>

            <TabsContent value="microsoft">
              <MicrosoftOAuthSetup
                onSuccess={() => handleSuccess("Microsoft")}
                onError={handleError}
                redirectUri={redirectUri}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card className="border-2 border-gray-200 bg-card">
        <CardHeader className="bg-card-50">
          <CardTitle className="flex items-center space-x-2 text-gray-800">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>Setup Instructions</span>
          </CardTitle>
          <CardDescription className="text-gray-600">
            Follow these steps to connect your calendar accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Google Setup */}
            <div className="space-y-4 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-200 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-700" />
                </div>
                <h3 className="font-semibold text-lg text-blue-900">Google Calendar Setup</h3>
              </div>
              <div className="space-y-3 pl-12">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
                  <p className="text-sm text-blue-800">Click "Connect Primary Google Account" to start</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
                  <p className="text-sm text-blue-800">Sign in with your Google account</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">3</div>
                  <p className="text-sm text-blue-800">Grant calendar access permissions</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">4</div>
                  <p className="text-sm text-blue-800">Optionally connect a secondary account</p>
                </div>
              </div>
            </div>

            {/* Microsoft Setup */}
            <div className="space-y-4 p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-200 rounded-lg">
                  <Calendar className="h-5 w-5 text-orange-700" />
                </div>
                <h3 className="font-semibold text-lg text-orange-900">Microsoft Calendar Setup</h3>
              </div>
              <div className="space-y-3 pl-12">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
                  <p className="text-sm text-orange-800">Click "Connect Microsoft Account" to start</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
                  <p className="text-sm text-orange-800">Sign in with your Microsoft account</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-medium">3</div>
                  <p className="text-sm text-orange-800">Grant calendar access permissions</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-medium">4</div>
                  <p className="text-sm text-orange-800">Your calendar will be available in UnifiedTime</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmRemove}
        title={confirmationModal.title}
        description={confirmationModal.description}
        confirmText="Remove Connection"
        cancelText="Cancel"
        variant="destructive"
        isLoading={removingConnection !== null}
        icon={<Trash2 className="h-6 w-6 text-red-600" />}
      />
    </div>
  );
}
